import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Alert,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  FormControlLabel,
  DialogContentText,
  CircularProgress,
  Autocomplete,
  ListItemButton,
  Checkbox,
  Menu,
  Popover,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Gavel,
  Add,
  FilterList,
  Search,
  MoreVert,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  AttachFile,
  Message,
  Warning,
  CheckCircle,
  Schedule,
  Person,
  DirectionsCar,
  MonetizationOn,
  Security,
  Flag,
  Block,
  Visibility,
  Edit,
  Close,
  Send,
  Download,
  Image,
  Phone,
  Email,
  AccessTime,
  PriorityHigh,
  Assignment,
  NotificationImportant,
  ReportProblem,
  Verified,
  Cancel,
  Info,
  SaveAlt,
  Restore,
  Delete,
  FileDownload,
  PictureAsPdf,
  TableChart,
  History,
  Timer,
  Group,
  Done,
  DoneAll,
  CloseOutlined,
  ArrowForward,
  CalendarToday,
  TrendingUp,
  Science,
  PlayArrow,
} from '@mui/icons-material';
import { disputeService, type Dispute as APIDispute, type DisputeStatistics } from '@/services/disputeService';
import toast from 'react-hot-toast';
import { VerificationCodeSection } from '@/components/disputes/VerificationCodeSection';
import { CheckinCheckoutSection } from '@/components/disputes/CheckinCheckoutSection';
import { AdminBookingDetailsModal } from '@/components/bookings/AdminBookingDetailsModal';

interface Dispute {
  id: string;
  bookingId: string;
  type: 'Damage' | 'Cancellation' | 'Payment' | 'Substitution' | 'Late Return' | 'Cleanliness' | 'Driver';
  priority: 'High Priority' | 'Medium Priority' | 'Low Priority';
  status: 'Open' | 'Under Review' | 'Resolved' | 'Escalated';
  title: string;
  description: string;
  amount: number;
  plaintiff: {
    name: string;
    type: 'Host' | 'Renter' | 'Driver';
    avatar?: string;
  };
  defendant: {
    name: string;
    type: 'Host' | 'Renter' | 'Driver';
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
  evidence: {
    photos: string[];
    documents: string[];
  };
  resolution?: string;
  adminNotes?: string;
}

interface ReviewItem {
  id: string;
  type: 'Inappropriate' | 'False Claims' | 'Contact Info' | 'Harassment';
  content: string;
  reporter: string;
  reportedUser: string;
  platform: string;
  createdAt: string;
  status: 'Pending' | 'Reviewed' | 'Action Taken';
  severity: 'Low' | 'Medium' | 'High';
}

interface Communication {
  id: string;
  sender: string;
  senderType: 'Host' | 'Renter' | 'Support';
  message: string;
  timestamp: string;
  isAdmin?: boolean;
}

export const DisputesPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDispute, setSelectedDispute] = useState<APIDispute | null>(null);
  const [disputeDetailsOpen, setDisputeDetailsOpen] = useState(false);
  const [caseStatus, setCaseStatus] = useState('pending');
  const [adminNote, setAdminNote] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [disputes, setDisputes] = useState<APIDispute[]>([]);
  const [statistics, setStatistics] = useState<DisputeStatistics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newCaseDialogOpen, setNewCaseDialogOpen] = useState(false);
  const [newCaseData, setNewCaseData] = useState({
    bookingId: '',
    type: 'damage' as const,
    priority: 'medium' as const,
    title: '',
    description: '',
    amount: '',
    filedById: '',
    filedAgainstId: '',
  });
  const [users, setUsers] = useState<Array<{ id: string; email: string; name?: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [bookings, setBookings] = useState<Array<any>>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // New Feature States
  // 1. Bulk Actions & Multi-Select
  const [selectedDisputes, setSelectedDisputes] = useState<string[]>([]);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);

  // 2. Advanced Filtering
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: '',
    assignedTo: 'all',
    status: 'all',
  });

  // 3. Quick Actions Menu
  const [quickActionAnchor, setQuickActionAnchor] = useState<null | HTMLElement>(null);
  const [quickActionDispute, setQuickActionDispute] = useState<APIDispute | null>(null);

  // 5. Dispute Assignment
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignToAdmin, setAssignToAdmin] = useState('');
  const [adminsList, setAdminsList] = useState<Array<{ id: string; name: string; workload: number }>>([
    { id: 'admin1', name: 'John Admin', workload: 5 },
    { id: 'admin2', name: 'Jane Moderator', workload: 3 },
    { id: 'admin3', name: 'Mike Support', workload: 7 },
  ]);

  // 6. Booking Details Modal (for viewing full booking details)
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // 7. Test Data Initialization
  const [testDataDialogOpen, setTestDataDialogOpen] = useState(false);
  const [testDataBookingId, setTestDataBookingId] = useState('');

  // Load disputes from API
  const loadDisputes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: any = {};
      if (filterPriority !== 'all') filters.priority = filterPriority;
      if (filterType !== 'all') filters.type = filterType.toLowerCase();
      if (searchTerm) filters.search = searchTerm;

      const result = await disputeService.getDisputes(filters);

      if (result.success && result.data) {
        // Sort disputes by creation date (newest first)
        const sortedDisputes = [...result.data.disputes].sort((a, b) => {
          const dateA = new Date((a as any).createdAt || a.created_at).getTime();
          const dateB = new Date((b as any).createdAt || b.created_at).getTime();
          return dateB - dateA; // Newest first
        });
        setDisputes(sortedDisputes);
        setStatistics(result.data.statistics);
      } else {
        // Even on error, set empty data to prevent UI crash
        if (result.data) {
          const sortedDisputes = [...result.data.disputes].sort((a, b) => {
            const dateA = new Date((a as any).createdAt || a.created_at).getTime();
            const dateB = new Date((b as any).createdAt || b.created_at).getTime();
            return dateB - dateA; // Newest first
          });
          setDisputes(sortedDisputes);
          setStatistics(result.data.statistics);
        }
        const errorMessage = result.error || 'Failed to load disputes';
        setError(`Backend API Error: ${errorMessage}. The disputes endpoint may not be implemented or is experiencing issues.`);
        console.error('Disputes API Error:', errorMessage);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load disputes';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount and when filters change
  useEffect(() => {
    loadDisputes();
  }, [filterPriority, filterType, searchTerm]);

  // Filter disputes based on status (already sorted by loadDisputes)
  const openDisputes = disputes.filter(d => d.status === 'pending' || d.status === 'investigating');
  const resolvedDisputes = disputes.filter(d => d.status === 'resolved' || d.status === 'closed');

  // Helper function to format type display
  const formatType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to format priority display
  const formatPriority = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  // Mock review queue - keeping as is since backend doesn't have this yet
  const reviewQueue: ReviewItem[] = [
    {
      id: '1',
      type: 'Inappropriate',
      content: 'This host was completely unprofessional and tried to [...]',
      reporter: 'By: Lisa M. on BMW 5 Series',
      reportedUser: 'Review #412',
      platform: 'Flagged Content',
      createdAt: '3 hrs ago',
      status: 'Pending',
      severity: 'Medium'
    },
    {
      id: '2',
      type: 'False Claims',
      content: 'The car had significant damage that wasn\'t disclosed and made me feel [...]',
      reporter: 'By: Mark T. on Mercedes GLC',
      reportedUser: 'Review #410',
      platform: 'Flagged Content',
      createdAt: '5 hrs ago',
      status: 'Pending',
      severity: 'High'
    },
    {
      id: '3',
      type: 'Contact Info',
      content: 'Great experience! Contact me at 555-123-4567 for [...]',
      reporter: 'By: Chris J. on Range Rover',
      reportedUser: 'Review #409',
      platform: 'Flagged Content',
      createdAt: '7 hrs ago',
      status: 'Pending',
      severity: 'Low'
    },
    {
      id: '4',
      type: 'Harassment',
      content: 'The driver was extremely rude and made me feel uncomfortable [...]',
      reporter: 'By: Nicole P. on Tesla Model Y',
      reportedUser: 'Review #407',
      platform: 'Flagged Content',
      createdAt: '10 hrs ago',
      status: 'Pending',
      severity: 'High'
    }
  ];

  const communications: Communication[] = [
    {
      id: '1',
      sender: 'John D. (Renter)',
      senderType: 'Renter',
      message: 'I did not cause this damage. The car was already scratched when I picked it up.',
      timestamp: 'May 15, 2:45 PM'
    },
    {
      id: '2',
      sender: 'Michael R. (Host)',
      senderType: 'Host',
      message: 'The pre-rental photos clearly show no damage. These scratches are new and should be covered by the renter.',
      timestamp: 'May 15, 3:15 PM'
    }
  ];

  const handleDisputeClick = async (dispute: APIDispute) => {
    try {
      // Load full details including activity timeline
      const result = await disputeService.getDisputeDetails(dispute.id);

      if (result.success && result.data) {

        setSelectedDispute({ ...result.data.dispute, activity: result.data.activity });
        setCaseStatus(result.data.dispute.status);
        setDisputeDetailsOpen(true);
      } else {
        toast.error('Failed to load dispute details');
      }
    } catch (err) {
      toast.error('Failed to load dispute details');
    }
  };

  const handleCloseDetails = () => {
    setDisputeDetailsOpen(false);
    setSelectedDispute(null);
  };

  const handleViewBookingDetails = () => {
    // Check multiple possible locations for booking_id
    const bookingId = (selectedDispute as any)?.booking_id
      || (selectedDispute as any)?.bookingId
      || (selectedDispute as any)?.booking?.id;


    if (bookingId) {
      setSelectedBookingId(bookingId);
      setBookingDetailsOpen(true);
    } else {
      console.error('❌ No booking ID found in dispute:', selectedDispute);
      toast.error('No booking ID found for this dispute');
    }
  };

  const handleReviewAction = (action: string, itemId: string) => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    setIsLoading(true);

    setTimeout(() => {
      toast.success(`Action '${confirmAction}' completed successfully`);
      setIsLoading(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }, 1000);
  };

  const handleNewCase = async () => {
    setNewCaseDialogOpen(true);

    // Load users and bookings when dialog opens
    await Promise.all([loadUsers(), loadBookings()]);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.users) {
          setUsers(result.data.users);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users list');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // The bookings API returns data as an array, not { bookings: [] }
          setBookings(Array.isArray(result.data) ? result.data : []);
        }
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings list');
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleFilters = () => {
    toast('Advanced filters feature coming soon', { icon: 'ℹ️' });
  };

  const handleCloseNewCaseDialog = () => {
    setNewCaseDialogOpen(false);
    // Reset form
    setNewCaseData({
      bookingId: '',
      type: 'damage',
      priority: 'medium',
      title: '',
      description: '',
      amount: '',
      filedById: '',
      filedAgainstId: '',
    });
    // Clear lists
    setUsers([]);
    setBookings([]);
  };

  const handleCreateCase = async () => {
    // Validation (booking ID is now optional)
    const missingFields = [];
    if (!newCaseData.title) missingFields.push('Case Title');
    if (!newCaseData.description) missingFields.push('Description');
    if (!newCaseData.filedById) missingFields.push('Filed By (User)');
    if (!newCaseData.filedAgainstId) missingFields.push('Filed Against (User)');

    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(', ')}`, { duration: 5000 });
      return;
    }

    setIsLoading(true);

    try {
      // Build request body - only include booking_id if provided
      const requestBody: any = {
        type: newCaseData.type,
        priority: newCaseData.priority,
        title: newCaseData.title,
        description: newCaseData.description,
        filed_by_id: newCaseData.filedById,
        filed_against_id: newCaseData.filedAgainstId,
      };

      // Add optional fields only if they have values
      if (newCaseData.bookingId) {
        requestBody.booking_id = newCaseData.bookingId;
      }
      if (newCaseData.amount) {
        requestBody.amount = parseFloat(newCaseData.amount);
      }

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/disputes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Check if it's a 405 Method Not Allowed
        if (response.status === 405) {
          toast.error('Backend API does not support creating disputes yet. The POST endpoint needs to be implemented.', { duration: 6000 });
          setIsLoading(false);
          return;
        }

        // Check if backend is complaining about booking_id being required
        if (errorData.message && errorData.message.includes('booking_id') && errorData.message.includes('required')) {
          toast.error('Backend API still requires booking_id. Please ask your backend team to update the POST /api/v1/admin/disputes endpoint to make booking_id optional (see BACKEND-IMPLEMENTATION-GUIDE.md)', { duration: 8000 });
          setIsLoading(false);
          return;
        }

        // Show the specific error message from backend
        throw new Error(errorData.message || errorData.error || 'Failed to create dispute');
      }

      const result = await response.json();

      if (result.success) {
        toast.success('Dispute case created successfully');
        handleCloseNewCaseDialog();
        await loadDisputes(); // Reload the disputes list
      } else {
        toast.error(result.message || result.error || 'Failed to create dispute');
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create dispute case');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!adminNote.trim() || !selectedDispute) return;

    try {
      const result = await disputeService.addNote(selectedDispute.id, adminNote.trim());

      if (result.success) {
        toast.success('Admin note saved successfully');
        setAdminNote('');
        // Reload dispute details
        await handleDisputeClick(selectedDispute);
      } else {
        toast.error(result.error || 'Failed to save note');
      }
    } catch (err) {
      toast.error('Failed to save note');
    }
  };

  const handleResolutionAction = (action: string) => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute) return;

    try {
      setIsLoading(true);
      const result = await disputeService.resolveDispute(selectedDispute.id, {
        resolution: 'Resolved by admin',
        resolutionNotes: adminNote || undefined,
        closeDispute: true
      });

      if (result.success) {
        toast.success('Dispute resolved successfully');
        setDisputeDetailsOpen(false);
        setSelectedDispute(null);
        await loadDisputes();
      } else {
        toast.error(result.error || 'Failed to resolve dispute');
      }
    } catch (err) {
      toast.error('Failed to resolve dispute');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    const lowercasePriority = priority.toLowerCase();
    if (lowercasePriority.includes('high') || lowercasePriority === 'urgent') return 'error';
    if (lowercasePriority.includes('medium')) return 'warning';
    if (lowercasePriority.includes('low')) return 'info';
    return 'default';
  };

  const getTypeIcon = (type: string) => {
    const lowercaseType = type.toLowerCase();
    if (lowercaseType === 'damage') return <Warning />;
    if (lowercaseType === 'cancellation') return <Cancel />;
    if (lowercaseType === 'payment') return <MonetizationOn />;
    if (lowercaseType === 'substitution') return <DirectionsCar />;
    if (lowercaseType.includes('late')) return <Schedule />;
    if (lowercaseType === 'cleanliness') return <Info />;
    if (lowercaseType.includes('driver')) return <Person />;
    return <ReportProblem />;
  };

  const getStatusColor = (status: string) => {
    const lowercaseStatus = status.toLowerCase();
    if (lowercaseStatus === 'pending' || lowercaseStatus === 'open') return 'error';
    if (lowercaseStatus === 'investigating' || lowercaseStatus.includes('review')) return 'warning';
    if (lowercaseStatus === 'resolved') return 'success';
    if (lowercaseStatus === 'closed') return 'default';
    return 'default';
  };

  // === NEW FEATURE HANDLERS ===

  // 1. Bulk Actions & Multi-Select
  const handleSelectDispute = (disputeId: string) => {
    setSelectedDisputes(prev =>
      prev.includes(disputeId) ? prev.filter(id => id !== disputeId) : [...prev, disputeId]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedDisputes(checked ? openDisputes.map(d => d.id) : []);
  };

  const handleBulkAction = async (action: string) => {
    if (selectedDisputes.length === 0) {
      toast.error('Please select at least one dispute');
      return;
    }

    setIsLoading(true);
    try {
      // Simulate bulk API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`${action} applied to ${selectedDisputes.length} dispute(s)`);
      setSelectedDisputes([]);
      setBulkActionAnchor(null);
      await loadDisputes();
    } catch (error) {
      toast.error(`Failed to ${action}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 2. Advanced Filtering
  const handleAdvancedFilter = () => {
    setAdvancedFilterOpen(true);
  };

  const applyAdvancedFilters = () => {
    setAdvancedFilterOpen(false);
    loadDisputes(); // Reload with new filters
    toast.success('Filters applied successfully');
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: '',
      assignedTo: 'all',
      status: 'all',
    });
  };

  // 3. Quick Actions
  const handleQuickAction = (event: React.MouseEvent<HTMLElement>, dispute: APIDispute) => {
    event.stopPropagation();
    setQuickActionAnchor(event.currentTarget);
    setQuickActionDispute(dispute);
  };

  const executeQuickAction = async (action: string) => {
    if (!quickActionDispute) return;

    setQuickActionAnchor(null);
    setIsLoading(true);

    try {
      switch (action) {
        case 'assign':
          setSelectedDispute(quickActionDispute);
          setAssignmentDialogOpen(true);
          break;
        case 'investigate':
          await disputeService.updateDispute(quickActionDispute.id, { status: 'investigating' });
          toast.success('Dispute status updated to Investigating');
          await loadDisputes();
          break;
        case 'resolve':
          await disputeService.resolveDispute(quickActionDispute.id, {
            resolution: 'Quick resolved',
            closeDispute: true
          });
          toast.success('Dispute resolved successfully');
          await loadDisputes();
          break;
        default:
          toast(`Action ${action} executed`);
      }
    } catch (error) {
      toast.error(`Failed to execute action: ${action}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 5. Dispute Assignment
  const handleAssignDispute = async () => {
    if (!assignToAdmin) {
      toast.error('Please select an admin');
      return;
    }

    setIsLoading(true);
    try {
      // Simulate assignment API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`Dispute assigned to ${adminsList.find(a => a.id === assignToAdmin)?.name}`);
      setAssignmentDialogOpen(false);
      setAssignToAdmin('');
      await loadDisputes();
    } catch (error) {
      toast.error('Failed to assign dispute');
    } finally {
      setIsLoading(false);
    }
  };

  // 6. Export & Reporting
  const exportToCSV = () => {
    const csvData = openDisputes.map(d => ({
      ID: d.id,
      Title: d.title,
      Type: d.type,
      Priority: d.priority,
      Status: d.status,
      Amount: (d as any).amount || 0,
      Created: new Date((d as any).createdAt).toLocaleDateString(),
    }));

    const headers = Object.keys(csvData[0] || {}).join(',');
    const rows = csvData.map(row => Object.values(row).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `disputes-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success('Exported to CSV successfully');
  };

  const exportToPDF = () => {
    toast('PDF export feature coming soon - requires PDF library integration');
  };

  // 7. SLA Tracking
  const getSLAStatus = (dispute: APIDispute) => {
    const createdAt = new Date((dispute as any).createdAt || dispute.created_at).getTime();
    const now = Date.now();
    const hoursOpen = (now - createdAt) / (1000 * 60 * 60);

    // SLA thresholds based on priority
    const slaThresholds = {
      urgent: 4,
      high: 24,
      medium: 48,
      low: 72
    };

    const threshold = slaThresholds[dispute.priority.toLowerCase() as keyof typeof slaThresholds] || 48;
    const percentUsed = (hoursOpen / threshold) * 100;

    return {
      hoursOpen: Math.floor(hoursOpen),
      hoursRemaining: Math.max(0, Math.floor(threshold - hoursOpen)),
      percentUsed: Math.min(100, percentUsed),
      isBreached: percentUsed > 100,
      isNearBreach: percentUsed > 80 && percentUsed <= 100,
      isOnTrack: percentUsed <= 80,
    };
  };

  // Handlers for verification and check-in/checkout

  // Test data initialization handler
  const handleInitializeTestData = async () => {
    if (!testDataBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }

    setIsLoading(true);
    try {
      // API_BASE is now dynamic
      const url = `${getApiBaseUrl()}/api/v1/admin/test-data/initialize-verification`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingId: testDataBookingId.trim() })
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }

        const errorMessage = errorData.message || errorData.error || `Server error (${response.status})`;
        throw new Error(errorMessage);
      }

      const data = await response.json();

      toast.success(`Test data initialized! Verification code: ${data.data?.verificationCode || 'N/A'}`);
      setTestDataDialogOpen(false);
      setTestDataBookingId('');

      // Reload disputes to show updated data
      await loadDisputes();
    } catch (error) {
      console.error('❌ Error initializing test data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize test data';
      toast.error(`Failed to initialize: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Disputes & Moderation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user issues and content moderation
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Bulk Actions Indicator */}
          {selectedDisputes.length > 0 && (
            <Chip
              label={`${selectedDisputes.length} selected`}
              color="primary"
              onDelete={() => setSelectedDisputes([])}
              deleteIcon={<CloseOutlined />}
            />
          )}

          {/* Export Buttons */}
          <Tooltip title="Export to CSV">
            <IconButton onClick={exportToCSV} color="primary">
              <TableChart />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to PDF">
            <IconButton onClick={exportToPDF} color="primary">
              <PictureAsPdf />
            </IconButton>
          </Tooltip>

          {/* Bulk Actions */}
          {selectedDisputes.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DoneAll />}
              onClick={(e) => setBulkActionAnchor(e.currentTarget)}
              sx={{ textTransform: 'none' }}
            >
              Bulk Actions
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ textTransform: 'none' }}
            onClick={handleAdvancedFilter}
            data-testid="filter-disputes-btn"
          >
            Advanced Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Science />}
            sx={{ textTransform: 'none' }}
            onClick={() => setTestDataDialogOpen(true)}
            color="secondary"
            data-testid="init-test-data-btn"
          >
            Init Test Data
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={handleNewCase}
            data-testid="new-dispute-case-btn"
          >
            New Case
          </Button>
        </Box>
      </Box>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionAnchor}
        open={Boolean(bulkActionAnchor)}
        onClose={() => setBulkActionAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkAction('Assign')}>
          <Group sx={{ mr: 1 }} /> Assign to Admin
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('Mark as Investigating')}>
          <Search sx={{ mr: 1 }} /> Mark as Investigating
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('Resolve')}>
          <Done sx={{ mr: 1 }} /> Resolve All
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('Close')}>
          <CloseOutlined sx={{ mr: 1 }} /> Close All
        </MenuItem>
      </Menu>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Open Disputes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selectedDisputes.length === openDisputes.length && openDisputes.length > 0}
                    indeterminate={selectedDisputes.length > 0 && selectedDisputes.length < openDisputes.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    size="small"
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Open Disputes ({openDisputes.length})
                  </Typography>
                </Box>
                <Chip
                  label={`${(statistics as any)?.urgent || 0} Urgent`}
                  size="small"
                  color="error"
                />
              </Box>

              {/* Filters */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
                <TextField
                  size="small"
                  placeholder="Search disputes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="damage">Damage</MenuItem>
                      <MenuItem value="payment">Payment</MenuItem>
                      <MenuItem value="cancellation">Cancellation</MenuItem>
                      <MenuItem value="substitution">Substitution</MenuItem>
                      <MenuItem value="late_return">Late Return</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Priority</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Disputes List */}
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {isLoading && !disputes.length ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : openDisputes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No open disputes found
                  </Typography>
                ) : (
                  openDisputes.map((dispute) => {
                    const slaStatus = getSLAStatus(dispute);
                    return (
                    <Card
                      key={dispute.id}
                      elevation={selectedDisputes.includes(dispute.id) ? 4 : 1}
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.2s ease-in-out',
                        borderLeft: slaStatus.isBreached ? '4px solid' : '4px solid transparent',
                        borderLeftColor: slaStatus.isBreached ? 'error.main' : 'transparent',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                          borderLeftColor: slaStatus.isBreached ? 'error.main' : 'primary.main',
                        },
                      }}
                      onClick={() => handleDisputeClick(dispute)}
                    >
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        {/* Header with Checkbox, Title and Priority */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
                          <Checkbox
                            checked={selectedDisputes.includes(dispute.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectDispute(dispute.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ p: 0, mt: 0.25 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.9rem',
                                  flex: 1,
                                  mr: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {(dispute as any).bookingNumber ? `Booking ${(dispute as any).bookingNumber}` :
                                 (dispute as any).bookingId ? `Booking ${(dispute as any).bookingId}` :
                                 'General Dispute'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                <Chip
                                  label={formatPriority(dispute.priority)}
                                  size="small"
                                  color={getPriorityColor(dispute.priority) as any}
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleQuickAction(e, dispute)}
                                  sx={{
                                    ml: 0.5,
                                    '&:hover': { backgroundColor: 'action.hover' }
                                  }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Type Badge with Icon */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  backgroundColor: 'action.hover',
                                }}
                              >
                                {getTypeIcon(dispute.type)}
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {formatType(dispute.type)}
                                </Typography>
                              </Box>
                              <Chip
                                label={dispute.status.toUpperCase()}
                                size="small"
                                variant="outlined"
                                color={getStatusColor(dispute.status) as any}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.75 }
                                }}
                              />
                            </Box>

                            {/* SLA Indicator */}
                            {slaStatus.isBreached && (
                              <Alert
                                severity="error"
                                sx={{
                                  py: 0.5,
                                  px: 1.5,
                                  mb: 1.5,
                                  '& .MuiAlert-icon': { mr: 1, fontSize: 18 }
                                }}
                                icon={<Timer fontSize="small" />}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                  SLA BREACHED • {slaStatus.hoursOpen}h open
                                </Typography>
                              </Alert>
                            )}
                            {slaStatus.isNearBreach && !slaStatus.isBreached && (
                              <Box
                                sx={{
                                  mb: 1.5,
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: 'warning.lighter',
                                  border: '1px solid',
                                  borderColor: 'warning.light'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Timer fontSize="inherit" />
                                    {slaStatus.hoursRemaining}h remaining
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                                    {Math.round(slaStatus.percentUsed)}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={slaStatus.percentUsed}
                                  color="warning"
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'warning.lighter'
                                  }}
                                />
                              </Box>
                            )}

                            {/* Title/Description */}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.4,
                                fontSize: '0.8rem'
                              }}
                            >
                              {dispute.title}
                            </Typography>

                            {/* Footer with metadata */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                pt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {new Date((dispute as any).createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                              {(dispute as any).assignedTo ? (
                                <Chip
                                  avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>{(dispute as any).assignedTo.name.charAt(0)}</Avatar>}
                                  label={`${(dispute as any).assignedTo.name}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.7rem',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Unassigned"
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.7rem',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Resolved Disputes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Resolved Disputes ({resolvedDisputes.length})
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select defaultValue="Last 30 Days" displayEmpty>
                    <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                    <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                    <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {isLoading && !disputes.length ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : resolvedDisputes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No resolved disputes found
                  </Typography>
                ) : (
                  resolvedDisputes.map((dispute) => (
                    <Card
                      key={dispute.id}
                      elevation={1}
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        borderLeft: '4px solid',
                        borderLeftColor: 'success.main',
                        opacity: 0.95,
                        '&:hover': {
                          opacity: 1,
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        }
                      }}
                      onClick={() => handleDisputeClick(dispute)}
                    >
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {(dispute as any).bookingNumber ? `Booking ${(dispute as any).bookingNumber}` :
                               (dispute as any).bookingId ? `Booking ${(dispute as any).bookingId}` :
                               'General Dispute'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main', fontSize: '0.7rem' }}>
                                {dispute.resolution || 'RESOLVED'}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            icon={<Done sx={{ fontSize: 14 }} />}
                            label={formatPriority(dispute.priority)}
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{
                              height: 24,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 0.75 }
                            }}
                          />
                        </Box>

                        {/* Type Badge */}
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            mb: 1.5,
                            borderRadius: 1,
                            backgroundColor: 'success.lighter',
                            border: '1px solid',
                            borderColor: 'success.light'
                          }}
                        >
                          {getTypeIcon(dispute.type)}
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark', fontSize: '0.7rem' }}>
                            {formatType(dispute.type)}
                          </Typography>
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.4,
                            fontSize: '0.8rem'
                          }}
                        >
                          {dispute.title}
                        </Typography>

                        {/* Footer */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {(dispute as any).resolvedAt
                                ? new Date((dispute as any).resolvedAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : 'N/A'}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Review Queue */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Review Queue ({reviewQueue.length})
                </Typography>
                <Chip label="Flagged Content" size="small" color="warning" />
              </Box>

              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {reviewQueue.map((item) => (
                  <Card
                    key={item.id}
                    elevation={1}
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      borderLeft: '4px solid',
                      borderLeftColor: item.severity === 'High' ? 'error.main' : item.severity === 'Medium' ? 'warning.main' : 'info.main',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.reportedUser}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Flag sx={{ fontSize: 12, color: 'warning.main' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', fontSize: '0.7rem' }}>
                              {item.platform}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <Chip
                            label={item.type}
                            size="small"
                            color={item.severity === 'High' ? 'error' : item.severity === 'Medium' ? 'warning' : 'info'}
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Severity Indicator */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.25,
                          mb: 1.5,
                          borderRadius: 1,
                          backgroundColor: item.severity === 'High' ? 'error.lighter' : item.severity === 'Medium' ? 'warning.lighter' : 'info.lighter',
                          border: '1px solid',
                          borderColor: item.severity === 'High' ? 'error.light' : item.severity === 'Medium' ? 'warning.light' : 'info.light'
                        }}
                      >
                        <PriorityHigh sx={{ fontSize: 14, color: item.severity === 'High' ? 'error.dark' : item.severity === 'Medium' ? 'warning.dark' : 'info.dark' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            color: item.severity === 'High' ? 'error.dark' : item.severity === 'Medium' ? 'warning.dark' : 'info.dark'
                          }}
                        >
                          {item.severity} Severity
                        </Typography>
                      </Box>

                      {/* Content Quote */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          mb: 1.5,
                          backgroundColor: 'action.hover',
                          borderLeft: '3px solid',
                          borderLeftColor: 'divider'
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontStyle: 'italic',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.4,
                            fontSize: '0.75rem'
                          }}
                        >
                          "{item.content}"
                        </Typography>
                      </Paper>

                      {/* Reporter Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {item.reporter}
                        </Typography>
                      </Box>

                      {/* Footer with Actions */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          pt: 1.5,
                          borderTop: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {item.createdAt}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReviewAction('hide', item.id);
                            }}
                            sx={{
                              fontSize: '0.7rem',
                              px: 1.5,
                              py: 0.5,
                              textTransform: 'none',
                              fontWeight: 600
                            }}
                          >
                            Hide
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReviewAction('keep', item.id);
                            }}
                            sx={{
                              fontSize: '0.7rem',
                              px: 1.5,
                              py: 0.5,
                              textTransform: 'none',
                              fontWeight: 600
                            }}
                          >
                            Keep
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dispute Details Dialog */}
      <Dialog
        open={disputeDetailsOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Dispute Details: {(selectedDispute as any)?.bookingNumber ? `Booking ${(selectedDispute as any).bookingNumber}` :
                              (selectedDispute as any)?.bookingId ? `Booking ${(selectedDispute as any).bookingId}` :
                              'General Dispute'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={selectedDispute?.type}
              color={selectedDispute?.type === 'Damage' ? 'error' : 'default'}
            />
            <Chip
              label={selectedDispute?.priority}
              color={getPriorityColor(selectedDispute?.priority || '') as any}
            />
            <IconButton onClick={handleCloseDetails}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Grid container sx={{ height: '100%' }}>
            {/* Left Panel - Dispute Info */}
            <Grid item xs={12} md={8} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 3 }}>
                {/* Booking Information */}
                <Typography variant="h6" sx={{ mb: 2 }}>Booking Information</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                    <Typography variant="body2">
                      {(selectedDispute as any)?.booking_id || (selectedDispute as any)?.bookingId || (selectedDispute as any)?.booking?.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Booking Number:</Typography>
                    <Typography variant="body2">
                      {(selectedDispute as any)?.bookingNumber || (selectedDispute as any)?.booking_number || (selectedDispute as any)?.booking?.bookingNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Vehicle:</Typography>
                    <Typography variant="body2">{(selectedDispute as any)?.vehicle?.make} {(selectedDispute as any)?.vehicle?.model} ({(selectedDispute as any)?.vehicle?.year || 'N/A'})</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plate Number:</Typography>
                    <Typography variant="body2">{(selectedDispute as any)?.vehicle?.plate_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Filed By:</Typography>
                    <Typography variant="body2">{(selectedDispute as any)?.filed_by?.name || 'N/A'} ({(selectedDispute as any)?.filed_by?.type || 'N/A'})</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Filed Against:</Typography>
                    <Typography variant="body2">{(selectedDispute as any)?.filed_against?.name || 'N/A'} ({(selectedDispute as any)?.filed_against?.type || 'N/A'})</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Amount:</Typography>
                    <Typography variant="body2">₦{selectedDispute?.amount?.toLocaleString() || '0'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Created:</Typography>
                    <Typography variant="body2">{selectedDispute?.created_at ? new Date(selectedDispute.created_at).toLocaleString() : 'N/A'}</Typography>
                  </Grid>
                </Grid>

                {/* Link to Full Booking Details */}
                {((selectedDispute as any)?.booking_id || (selectedDispute as any)?.bookingId || (selectedDispute as any)?.booking?.id) && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DirectionsCar />}
                    onClick={handleViewBookingDetails}
                    sx={{ mb: 3 }}
                  >
                    📋 View Full Booking Details (Verification, Check-in/Check-out, etc.)
                  </Button>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Complaint Summary */}
                <Typography variant="h6" sx={{ mb: 2 }}>Dispute Details</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Title:</strong> {selectedDispute?.title || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  <strong>Description:</strong> {selectedDispute?.description || 'No description provided'}
                </Typography>

                {selectedDispute?.evidence_urls && selectedDispute.evidence_urls.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Evidence Submitted ({selectedDispute.evidence_urls.length})</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {selectedDispute.evidence_urls.map((url: string, index: number) => (
                        <Grid item xs={4} key={index}>
                          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.100', cursor: 'pointer' }} onClick={() => window.open(url, '_blank')}>
                            <img src={url} alt={`Evidence ${index + 1}`} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px' }} onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement!.innerHTML = `<div><Image sx={{ fontSize: 40, color: 'text.secondary' }} /><Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Evidence ${index + 1}</Typography></div>`;
                            }} />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Activity Timeline */}
                <Typography variant="h6" sx={{ mb: 2 }}>Activity Timeline</Typography>
                {(selectedDispute as any)?.activity && (selectedDispute as any).activity.length > 0 ? (
                  <List>
                    {(selectedDispute as any).activity.map((activity: any, index: number) => (
                      <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            <History sx={{ fontSize: 16 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle2">{activity.action_type?.replace('_', ' ').toUpperCase() || 'Activity'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {activity.created_at ? new Date(activity.created_at).toLocaleString() : 'N/A'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {activity.description || 'No description'}
                              {activity.performed_by && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>by {activity.performed_by}</Typography>}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No activity recorded yet</Alert>
                )}
              </Box>
            </Grid>

            {/* Right Panel - Admin Actions */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Admin Actions</Typography>

                {/* Case Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Case Status</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={caseStatus}
                      onChange={async (e) => {
                        const newStatus = e.target.value as any;
                        if (selectedDispute) {
                          const result = await disputeService.updateDispute(selectedDispute.id, {
                            status: newStatus
                          });
                          if (result.success) {
                            setCaseStatus(newStatus);
                            toast.success('Status updated successfully');
                          } else {
                            toast.error('Failed to update status');
                          }
                        }
                      }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="investigating">Investigating</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Resolution Options */}
                <Typography variant="body2" sx={{ mb: 1 }}>Resolution Options</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Security />}
                    size="small"
                    fullWidth
                    onClick={() => handleResolutionAction('charge-security')}
                  >
                    Charge Security Deposit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Restore />}
                    size="small"
                    fullWidth
                    onClick={() => handleResolutionAction('partial-refund')}
                  >
                    Issue Partial Refund
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Block />}
                    size="small"
                    fullWidth
                    color="error"
                    onClick={() => handleResolutionAction('deny-claim')}
                  >
                    Deny Claim
                  </Button>
                </Box>

                {/* Admin Note */}
                <Typography variant="body2" sx={{ mb: 1 }}>Add Admin Note</Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  placeholder="Add internal notes about this case..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveAlt />}
                  fullWidth
                  sx={{ mb: 3 }}
                  onClick={handleSaveNote}
                  disabled={!adminNote.trim()}
                >
                  Save Note
                </Button>

                {/* Moderation Tools */}
                <Typography variant="body2" sx={{ mb: 1 }}>Moderation Tools</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<NotificationImportant />}
                    size="small"
                    fullWidth
                    onClick={() => toast('Notify feature coming soon', { icon: 'ℹ️' })}
                  >
                    Notify
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Assignment />}
                    size="small"
                    fullWidth
                    onClick={() => toast('Escalate feature coming soon', { icon: 'ℹ️' })}
                  >
                    Escalate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Flag />}
                    size="small"
                    fullWidth
                    color="warning"
                    onClick={() => toast('Flag user feature coming soon', { icon: '⚠️' })}
                  >
                    Flag User
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Block />}
                    size="small"
                    fullWidth
                    color="error"
                    onClick={() => toast.error('Suspend feature coming soon')}
                  >
                    Suspend
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                  onClick={handleResolveDispute}
                >
                  Resolve Dispute
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmAction}? This action may affect the dispute resolution process.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Case Dialog */}
      <Dialog
        open={newCaseDialogOpen}
        onClose={handleCloseNewCaseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Gavel color="primary" />
              <Typography variant="h6">Create New Dispute Case</Typography>
            </Box>
            <Chip
              label="Admin Tool"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            File a new dispute case for booking-related or general issues
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Booking Information */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DirectionsCar color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Booking Information (Optional)
                </Typography>
              </Box>
              <Autocomplete
                fullWidth
                options={bookings}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const bookingRef = option.bookingReference || option.bookingNumber || `BK${option.id?.substring(0, 8)}`;
                  const vehicleInfo = option.vehicle?.name || `${option.vehicle?.make || ''} ${option.vehicle?.model || ''}`.trim() || 'Vehicle';
                  const customerInfo = option.customer?.name || option.customer?.email || 'Customer';
                  const dateInfo = option.booking?.startDate
                    ? new Date(option.booking.startDate).toLocaleDateString()
                    : option.startDate
                    ? new Date(option.startDate).toLocaleDateString()
                    : 'Date N/A';
                  return `${bookingRef} - ${vehicleInfo} | ${customerInfo} | ${dateInfo}`;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and Select Booking"
                    placeholder="Type to search by booking number, vehicle, or customer..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBookings ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <ListItemButton {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {option.bookingReference || option.bookingNumber || `BK${option.id?.substring(0, 8)}`}
                        </Typography>
                        <Chip
                          label={option.status || 'N/A'}
                          size="small"
                          color={
                            option.status === 'confirmed' ? 'success' :
                            option.status === 'pending' ? 'warning' :
                            option.status === 'completed' ? 'info' :
                            'default'
                          }
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        <DirectionsCar sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        {option.vehicle?.name || `${option.vehicle?.make || ''} ${option.vehicle?.model || ''}`.trim() || 'Vehicle N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <Person sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                        {option.customer?.name || option.customer?.email || 'Customer N/A'}
                        {' • '}
                        {option.booking?.startDate
                          ? new Date(option.booking.startDate).toLocaleDateString()
                          : option.startDate
                          ? new Date(option.startDate).toLocaleDateString()
                          : 'Date N/A'}
                      </Typography>
                    </Box>
                  </ListItemButton>
                )}
                onChange={(event, newValue) => {
                  setNewCaseData({ ...newCaseData, bookingId: newValue?.id || '' });
                }}
                value={bookings.find(b => b.id === newCaseData.bookingId) || null}
                loading={loadingBookings}
                loadingText="Loading bookings..."
                noOptionsText={loadingBookings ? "Loading bookings..." : "No bookings found"}
                disabled={loadingBookings}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Alert severity="info" sx={{ mt: 1.5 }}>
                <Typography variant="caption">
                  <strong>Tip:</strong> Search by booking number, vehicle name, or customer name. Leave empty to create a general dispute not tied to a specific booking.
                </Typography>
              </Alert>
            </Paper>

            {/* Dispute Classification */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Assignment color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Dispute Classification
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Dispute Type *</InputLabel>
                    <Select
                      value={newCaseData.type}
                      label="Dispute Type *"
                      onChange={(e) => setNewCaseData({ ...newCaseData, type: e.target.value as any })}
                    >
                      <MenuItem value="damage">Damage</MenuItem>
                      <MenuItem value="payment">Payment</MenuItem>
                      <MenuItem value="cancellation">Cancellation</MenuItem>
                      <MenuItem value="substitution">Substitution</MenuItem>
                      <MenuItem value="late_return">Late Return</MenuItem>
                      <MenuItem value="cleanliness">Cleanliness</MenuItem>
                      <MenuItem value="driver_conduct">Driver Conduct</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority Level *</InputLabel>
                    <Select
                      value={newCaseData.priority}
                      label="Priority Level *"
                      onChange={(e) => setNewCaseData({ ...newCaseData, priority: e.target.value as any })}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Case Details */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Message color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Case Details
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Case Title *"
                placeholder="Brief summary of the issue"
                value={newCaseData.title}
                onChange={(e) => setNewCaseData({ ...newCaseData, title: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description *"
                placeholder="Detailed description of the dispute..."
                value={newCaseData.description}
                onChange={(e) => setNewCaseData({ ...newCaseData, description: e.target.value })}
                required
              />
            </Paper>

            {/* Financial Information */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <MonetizationOn color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Financial Information (Optional)
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Disputed Amount"
                type="number"
                placeholder="0.00"
                value={newCaseData.amount}
                onChange={(e) => setNewCaseData({ ...newCaseData, amount: e.target.value })}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography>
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                Enter the amount in dispute (if applicable)
              </Typography>
            </Paper>

            {/* Parties Involved */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Person color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Parties Involved
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Filed By (User) *</InputLabel>
                    <Select
                      value={newCaseData.filedById}
                      label="Filed By (User) *"
                      onChange={(e) => setNewCaseData({ ...newCaseData, filedById: e.target.value })}
                      disabled={loadingUsers}
                    >
                      {loadingUsers ? (
                        <MenuItem disabled>Loading users...</MenuItem>
                      ) : users.length === 0 ? (
                        <MenuItem disabled>No users available</MenuItem>
                      ) : (
                        users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name || user.email || user.id}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Select the person filing the dispute
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Filed Against (User) *</InputLabel>
                    <Select
                      value={newCaseData.filedAgainstId}
                      label="Filed Against (User) *"
                      onChange={(e) => setNewCaseData({ ...newCaseData, filedAgainstId: e.target.value })}
                      disabled={loadingUsers}
                    >
                      {loadingUsers ? (
                        <MenuItem disabled>Loading users...</MenuItem>
                      ) : users.length === 0 ? (
                        <MenuItem disabled>No users available</MenuItem>
                      ) : (
                        users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name || user.email || user.id}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Select the defendant
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Alert severity="warning" icon={<Info />}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Before Creating This Case
              </Typography>
              <Typography variant="caption" component="div">
                • All fields marked with * are required<br />
                • Booking selection is optional - you can create general disputes<br />
                • Cases start with "Pending" status and can be updated later<br />
                • Make sure you've selected the correct parties involved
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseNewCaseDialog}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCase}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <Add />}
          >
            {isLoading ? 'Creating...' : 'Create Case'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Actions Menu */}
      <Menu
        anchorEl={quickActionAnchor}
        open={Boolean(quickActionAnchor)}
        onClose={() => setQuickActionAnchor(null)}
      >
        <MenuItem onClick={() => executeQuickAction('assign')}>
          <Group sx={{ mr: 1 }} /> Assign to Admin
        </MenuItem>
        <MenuItem onClick={() => executeQuickAction('investigate')}>
          <Search sx={{ mr: 1 }} /> Mark as Investigating
        </MenuItem>
        <MenuItem onClick={() => executeQuickAction('resolve')}>
          <Done sx={{ mr: 1 }} /> Quick Resolve
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleDisputeClick(quickActionDispute!); setQuickActionAnchor(null); }}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
      </Menu>

      {/* Advanced Filters Dialog */}
      <Dialog
        open={advancedFilterOpen}
        onClose={() => setAdvancedFilterOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Advanced Filters</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Date Range */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Date Range
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={advancedFilters.dateFrom}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={advancedFilters.dateTo}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Amount Range */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Amount Range (₦)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min Amount"
                    type="number"
                    value={advancedFilters.amountMin}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, amountMin: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Amount"
                    type="number"
                    value={advancedFilters.amountMax}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, amountMax: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Assignment & Status */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Additional Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      value={advancedFilters.assignedTo}
                      label="Assigned To"
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, assignedTo: e.target.value })}
                    >
                      <MenuItem value="all">All Admins</MenuItem>
                      {adminsList.map(admin => (
                        <MenuItem key={admin.id} value={admin.id}>{admin.name}</MenuItem>
                      ))}
                      <MenuItem value="unassigned">Unassigned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={advancedFilters.status}
                      label="Status"
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="investigating">Investigating</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={clearAdvancedFilters}>Clear All</Button>
          <Button onClick={() => setAdvancedFilterOpen(false)}>Cancel</Button>
          <Button onClick={applyAdvancedFilters} variant="contained">Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Group color="primary" />
            <Typography variant="h6">Assign Dispute</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an admin to assign this dispute to. Workload balancing is shown to help distribute cases evenly.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Admin</InputLabel>
              <Select
                value={assignToAdmin}
                label="Select Admin"
                onChange={(e) => setAssignToAdmin(e.target.value)}
              >
                {adminsList.map(admin => (
                  <MenuItem key={admin.id} value={admin.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography>{admin.name}</Typography>
                      <Chip
                        label={`${admin.workload} cases`}
                        size="small"
                        color={admin.workload > 6 ? 'error' : admin.workload > 4 ? 'warning' : 'success'}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssignDispute} variant="contained" disabled={!assignToAdmin}>
            Assign Dispute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Data Initialization Dialog */}
      <Dialog open={testDataDialogOpen} onClose={() => setTestDataDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science color="primary" />
            <Typography variant="h6">Initialize Test Data</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3, mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Development/Staging Only
            </Typography>
            <Typography variant="caption">
              This will create verification codes and check-in/checkout test data for the specified booking.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Booking ID"
            placeholder="Enter booking UUID"
            value={testDataBookingId}
            onChange={(e) => setTestDataBookingId(e.target.value)}
            helperText="Enter a valid booking ID to initialize test data"
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              What will be created:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Verification Code"
                  secondary="6-digit code with verified status"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Check-in Data"
                  secondary="Photos, mileage, fuel level for both parties"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Check-out Data"
                  secondary="Photos and damage reports"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Dispute Record"
                  secondary="Sample checkout dispute"
                />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDataDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInitializeTestData}
            variant="contained"
            color="primary"
            disabled={!testDataBookingId.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {isLoading ? 'Initializing...' : 'Initialize Test Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Details Modal */}
      <AdminBookingDetailsModal
        open={bookingDetailsOpen}
        onClose={() => {
          setBookingDetailsOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        onBookingUpdated={() => {
          // Optionally reload disputes if booking was updated
          loadDisputes();
        }}
      />
    </Box>
  );
};